import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { Table, useLocalizeMessage, SearchField } from 'libs.nucleus';
export const DataSettings = forwardRef(({ items, onItemsChange }, ref) => {
    const translate = useLocalizeMessage();
    const [searchQuery, setSearchQuery] = useState('');
    // Store changes in a ref to avoid re-renders - only read on flush
    const changesRef = useRef(new Map());
    // Expose flush function to parent via ref
    useImperativeHandle(ref, () => ({
        flushPendingChanges: () => {
            // Apply all accumulated changes and notify parent
            if (changesRef.current.size === 0) {
                onItemsChange(items);
                return;
            }
            const updatedItems = items.map((item) => {
                const changes = changesRef.current.get(item.id);
                if (changes) {
                    return { ...item, ...changes };
                }
                return item;
            });
            changesRef.current.clear();
            onItemsChange(updatedItems);
        },
    }));
    const filteredItems = useMemo(() => {
        if (!searchQuery.trim()) {
            return items;
        }
        const query = searchQuery.toLowerCase();
        return items.filter((item) => item.name.toLowerCase().includes(query) ||
            item.description.toLowerCase().includes(query) ||
            item.category.toLowerCase().includes(query));
    }, [items, searchQuery]);
    // Handle changes by storing in ref (no state updates, no re-renders)
    const handleFieldChange = (id, field, value) => {
        const existing = changesRef.current.get(id) || {};
        changesRef.current.set(id, { ...existing, [field]: value });
    };
    const columns = useMemo(() => [
        {
            header: translate('Name'),
            accessorKey: 'name',
            size: 300,
            cell: ({ row }) => (_jsx("input", { type: 'text', id: `name-${row.original.id}`, defaultValue: row.original.name, onChange: (e) => handleFieldChange(row.original.id, 'name', e.target.value), className: 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent', autoComplete: 'off' })),
        },
        {
            header: translate('Description'),
            accessorKey: 'description',
            size: 300,
            cell: ({ row }) => (_jsx("input", { type: 'text', id: `description-${row.original.id}`, defaultValue: row.original.description || '', onChange: (e) => handleFieldChange(row.original.id, 'description', e.target.value), className: 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent', autoComplete: 'off' })),
        },
        {
            header: translate('Category'),
            accessorKey: 'category',
            size: 120,
            cell: ({ row }) => _jsx("div", { className: 'py-2', children: row.original.category }),
        },
    ], [translate]);
    return (_jsxs("div", { className: 'flex flex-col size-full gap-4', children: [_jsx("div", { className: 'font-semibold text-lg', children: translate('Configure each dimension and measure') }), _jsx("div", { className: 'max-w-md', children: _jsx(SearchField, { ariaLabel: translate('Search dimensions and measures'), placeholder: translate('Search...'), value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), onClear: () => setSearchQuery('') }) }), _jsx("div", { className: 'flex-1 overflow-auto', children: _jsx(Table, { data: filteredItems, columns: columns, labelNoResults: translate('No items found'), enableSorting: false, getRowId: (row) => row.id }) })] }));
});
DataSettings.displayName = 'DataSettings';
